import { getCompanyLastSelectedStateDataByCompanyId } from '../companyLastSelectedStates/mockData'
import {
  _getIDataById,
  getIndustryListData,
} from '../selectLists/industries/mockData'
import {
  _getSIDataById,
  getSubindustryListData,
} from '../selectLists/subindustries/mockData'
import { getTaskListDataByCompanyId } from '../tasks/mockData'
import { getCallListDataByCompanyId } from '../calls/mockData'
import { getEmailListDataByCompanyId } from '../emails/mockData'
import { getMeetingListDataByCompanyId } from '../meetings/mockData'
import { getOpportunityListDataByCompanyId } from '~/api/msw/api//opportunities/mockData'
import { getAccountListData, _getADataById } from '../accounts/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { getCompanyMasterServiceListDataByCompanyId } from '../companyMasterServices/mockData'
import {
  getContractListDataByCompanyId,
  _trimContractChildArraysToSilo,
} from '../contracts/mockData'
import { getCountryDataById } from '~/api/msw/api/selectLists/countries/mockData'
import { CountryIds } from '~/services/selectLists/countries'
import { getStateDataById } from '../selectLists/states/mockData'
import { getCompanyDocumentListDataByCompanyId } from '../companyDocuments/mockData'
import { getCompanyNoteListDataByCompanyId } from '../companyNotes/mockData'
import {
  getEmployeeCountById,
  EmployeeCountIds,
} from '~/services/selectLists/employeeCounts'
import {
  getCompanyStatusById,
  CompanyStatusIds,
} from '~/services/selectLists/companyStatii'
import {
  _getCompanyBillingCCTDataByCompanyId,
  _getCompanyPrimaryCCTDataByCompanyId,
  getCompanyContactListDataByCompanyId,
  getCompanyContactListData,
} from '~/api/msw/api/companyContacts/mockData'
import { getCompanyTeamMemberListDataByCompanyId } from '~/api/msw/api/companyTeamMembers/mockData'
import { getLocationListDataByCompanyId } from '~/api/msw/api/locations/mockData'
import type { TEntityId, IEntitySelectItem } from '~/types/models'
import type { ICompanyOverview, ICompany } from '@/types/company/models'

const companyListData = undefined as unknown as ICompany[]

export const getCompanyListData = getListDataFunctionFactory<ICompany[]>({
  listData: companyListData,
  storageKey: 'companies',
  getStartingListData: getStartingCompanyListData,
})

export function _getCDataById(id: TEntityId, list: ICompany[]): ICompany {
  if (!list) {
    throw new Error(`_getCDataById called with undefined/null list`)
  }
  const company = list.find((c: ICompany) => c.id === id)
  if (!company) {
    throw new Error(`Can't find company '${id}'`)
  }
  return company
}

export function _getCSelectItemDataById(
  id: TEntityId,
  list: ICompany[]
): IEntitySelectItem {
  if (!list) {
    throw new Error(`_getCSelectItemDataById called with undefined/null list`)
  }
  const company = _getCDataById(id, list)
  return { id: company.id, label: company.name }
}

export async function getCompanyDataById(id: string): Promise<ICompany> {
  const list = await getCompanyListData()
  return _getCDataById(id, list)
}

export async function getCompanySelectDataById(
  id: string
): Promise<IEntitySelectItem> {
  const list = await getCompanyListData()
  return _getCSelectItemDataById(id, list)
}

export async function getCompanyListDataByAccountId(
  id: string
): Promise<ICompany[]> {
  const list = await getCompanyListData()
  if (!list) {
    throw new Error(`companyList returned undefined/null`)
  }
  return list.filter((c: ICompany) => c.account.id === id) ?? []
}

export async function getCompanyListDataByAccountOwnerId(
  id: string
): Promise<ICompany[]> {
  const list = await getCompanyListData()
  if (!list) {
    throw new Error(`companyList returned undefined/null`)
  }
  return list.filter((c: ICompany) => c.account.owner.id === id) ?? []
}

export async function getCompanyOverviewData(
  id: string
): Promise<ICompanyOverview | undefined> {
  const theCompanyData = await getCompanyDataById(id)
  if (!theCompanyData) {
    throw new Error(`Company data for ${id} not found`)
  }
  // if (theCompanyData.address) {
  //   theCompanyData.address.country.label = theCompanyData.address?.country.name
  // }
  const EXCLUDE_CONTRACTS_WITH_MSSERVICES_NOT_YET_ADDED_TO_COMPANY = true
  const theOverviewData = {
    company: theCompanyData,
    companyMasterServices: await getCompanyMasterServiceListDataByCompanyId(id),
    locations: await getLocationListDataByCompanyId(id),
    // contacts: await getCompanyContactListDataByCompanyId(id), // deprecated. JR.
    companyContacts: await getCompanyContactListDataByCompanyId(id),
    companyTeamMembers: await getCompanyTeamMemberListDataByCompanyId(id),
    companyDocuments: await getCompanyDocumentListDataByCompanyId(id),
    companyNotes: await getCompanyNoteListDataByCompanyId(id),
    opportunities: await getOpportunityListDataByCompanyId(id),
    lastSelectedState: await getCompanyLastSelectedStateDataByCompanyId(id),
    projects: [],
    orderServiceOrders: [],
    serviceRequests: [],
    cases: [],
    contracts: _trimContractChildArraysToSilo(
      await getContractListDataByCompanyId(id),
      'company',
      id
    ),
    tasks: await getTaskListDataByCompanyId(id),
    calls: await getCallListDataByCompanyId(id),
    emails: await getEmailListDataByCompanyId(id),
    meetings: await getMeetingListDataByCompanyId(id),
  }
  return theOverviewData
}

export async function getStartingCompanyListData(): Promise<ICompany[]> {
  const cctList = await getCompanyContactListData()
  const aList = await getAccountListData()
  const iList = await getIndustryListData()
  const siList = await getSubindustryListData()
  const unitedStates = await getCountryDataById(CountryIds.UNITED_STATES)
  const massachusetts = await getStateDataById(1433)
  const wisconsin = await getStateDataById(1441)
  return [
    {
      id: 'XXC0001',
      hrid: 'C0001',
      name: 'Metropolis Bank',
      primaryContact: _getCompanyPrimaryCCTDataByCompanyId('XXC0001', cctList),
      billingContact: _getCompanyBillingCCTDataByCompanyId('XXC0001', cctList),
      account: _getADataById('XXA0001', aList),
      industry: _getIDataById('XXI0001', iList),
      subindustry: _getSIDataById('XXSI0001', siList),
      annualRevenue: 250000000,
      numEmployees: 2500,
      employeeCount: getEmployeeCountById(
        EmployeeCountIds.ONE_THOUSAND_ONE_TO_FIVE_THOUSAND
      ),
      hasLoaOnFile: true,
      loaExpirationDate: '2024-12-31',
      address: {
        country: unitedStates,
        addressLine1: '143 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      status: getCompanyStatusById(CompanyStatusIds.CUSTOMER),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXC0002',
      hrid: 'C0002',
      name: 'Far Reaching Health',
      primaryContact: _getCompanyPrimaryCCTDataByCompanyId('XXC0002', cctList),
      billingContact: _getCompanyBillingCCTDataByCompanyId('XXC0002', cctList),
      account: _getADataById('XXA0002', aList),
      industry: _getIDataById('XXI0002', iList),
      subindustry: _getSIDataById('XXSI0002', siList),
      annualRevenue: 500000000,
      numEmployees: 400,
      employeeCount: getEmployeeCountById(
        EmployeeCountIds.TWO_FIFTY_ONE_TO_FIVE_HUNDRED
      ),
      hasLoaOnFile: true,
      loaExpirationDate: '2024-12-31',
      address: {
        country: unitedStates,
        addressLine1: '234 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      status: getCompanyStatusById(CompanyStatusIds.CUSTOMER),
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXC0003',
      hrid: 'C0003',
      name: 'Boston Coach',
      primaryContact: _getCompanyPrimaryCCTDataByCompanyId('XXC0003', cctList),
      billingContact: _getCompanyBillingCCTDataByCompanyId('XXC0003', cctList),
      account: _getADataById('XXA0003', aList),
      industry: _getIDataById('XXI0005', iList),
      subindustry: _getSIDataById('XXSI0005', siList),
      annualRevenue: 250000000,
      numEmployees: 250,
      employeeCount: getEmployeeCountById(
        EmployeeCountIds.ONE_HUNRED_ONE_TO_TWO_FIFTY
      ),
      hasLoaOnFile: true,
      loaExpirationDate: '2024-12-31',
      address: {
        country: unitedStates,
        addressLine1: '456 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      status: getCompanyStatusById(CompanyStatusIds.PROPECT),
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXC0004',
      hrid: 'C0004',
      name: 'Boston Medical Center',
      primaryContact: _getCompanyPrimaryCCTDataByCompanyId('XXC0004', cctList),
      billingContact: _getCompanyBillingCCTDataByCompanyId('XXC0004', cctList),
      account: _getADataById('XXA0004', aList),
      industry: _getIDataById('XXI0002', iList),
      subindustry: _getSIDataById('XXSI0002', siList),
      annualRevenue: 500000000,
      numEmployees: 400,
      employeeCount: getEmployeeCountById(
        EmployeeCountIds.TWO_FIFTY_ONE_TO_FIVE_HUNDRED
      ),
      hasLoaOnFile: true,
      loaExpirationDate: '2024-12-31',
      address: {
        country: unitedStates,
        addressLine1: '567 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      status: getCompanyStatusById(CompanyStatusIds.PROPECT),
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXC0005',
      hrid: 'C0005',
      name: 'Full Rack Data Center',
      primaryContact: _getCompanyPrimaryCCTDataByCompanyId('XXC0005', cctList),
      billingContact: _getCompanyBillingCCTDataByCompanyId('XXC0005', cctList),
      account: _getADataById('XXA0005', aList),
      industry: _getIDataById('XXI0008', iList),
      subindustry: _getSIDataById('XXSI0008', siList),
      annualRevenue: 250000000,
      numEmployees: 250,
      employeeCount: getEmployeeCountById(
        EmployeeCountIds.ONE_HUNRED_ONE_TO_TWO_FIFTY
      ),
      hasLoaOnFile: true,
      loaExpirationDate: '2024-12-31',
      address: {
        country: unitedStates,
        addressLine1: '678 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      status: getCompanyStatusById(CompanyStatusIds.CUSTOMER),
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXC0006',
      hrid: 'C0006',
      name: 'Watch Company',
      primaryContact: _getCompanyPrimaryCCTDataByCompanyId('XXC0006', cctList),
      billingContact: _getCompanyBillingCCTDataByCompanyId('XXC0006', cctList),
      account: _getADataById('XXA0006', aList),
      industry: _getIDataById('XXI0005', iList),
      subindustry: _getSIDataById('XXSI0009', siList),
      annualRevenue: 500000000,
      numEmployees: 400,
      employeeCount: getEmployeeCountById(
        EmployeeCountIds.TWO_FIFTY_ONE_TO_FIVE_HUNDRED
      ),
      hasLoaOnFile: true,
      loaExpirationDate: '2024-12-31',
      address: {
        country: unitedStates,
        addressLine1: '789 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      status: getCompanyStatusById(CompanyStatusIds.PROPECT),
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXC0007',
      hrid: 'C0007',
      name: 'Stratham Coach',
      primaryContact: _getCompanyPrimaryCCTDataByCompanyId('XXC0007', cctList),
      billingContact: _getCompanyBillingCCTDataByCompanyId('XXC0007', cctList),
      account: _getADataById('XXA0007', aList),
      industry: _getIDataById('XXI0005', iList),
      subindustry: _getSIDataById('XXSI0005', siList),
      annualRevenue: 250000000,
      numEmployees: 250,
      employeeCount: getEmployeeCountById(
        EmployeeCountIds.ONE_HUNRED_ONE_TO_TWO_FIFTY
      ),
      hasLoaOnFile: false,
      loaExpirationDate: '--',
      address: {
        country: unitedStates,
        addressLine1: '143 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      status: getCompanyStatusById(CompanyStatusIds.PROPECT),
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXC0008',
      hrid: 'C0008',
      name: 'Durham Medical Center',
      primaryContact: _getCompanyPrimaryCCTDataByCompanyId('XXC0008', cctList),
      billingContact: _getCompanyBillingCCTDataByCompanyId('XXC0008', cctList),
      account: _getADataById('XXA0008', aList),
      industry: _getIDataById('XXI0002', iList),
      subindustry: _getSIDataById('XXSI0002', siList),
      annualRevenue: 450000000,
      numEmployees: 400,
      employeeCount: getEmployeeCountById(
        EmployeeCountIds.TWO_FIFTY_ONE_TO_FIVE_HUNDRED
      ),
      hasLoaOnFile: true,
      loaExpirationDate: '2024-12-31',
      address: {
        country: unitedStates,
        addressLine1: '143 Somewhere Street',
        city: 'Boston',
        state: massachusetts,
        zip: '04359',
      },
      status: getCompanyStatusById(CompanyStatusIds.PROPECT),
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXC0009',
      hrid: 'C0009',
      name: 'Alpaca Farms, Inc.',
      account: _getADataById('XXA0009', aList),
      industry: _getIDataById('XXI0005', iList),
      subindustry: _getSIDataById('XXSI0010', siList),
      address: {
        country: unitedStates,
        addressLine1: '69 Milk Street',
        addressLine2: '',
        city: 'Westborough',
        state: massachusetts,
        zip: '01581',
      },
      annualRevenue: 1,
      numEmployees: 300,
      employeeCount: getEmployeeCountById(
        EmployeeCountIds.TWO_FIFTY_ONE_TO_FIVE_HUNDRED
      ),
      hasLoaOnFile: true,
      loaExpirationDate: '2023-11-29',
      createdAt: '2023-11-15T17:32:00.000Z',
      modifiedAt: '2023-11-15T17:32:00.000Z',
      status: getCompanyStatusById(CompanyStatusIds.PROPECT),
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXC0010',
      hrid: 'C0010',
      address: {
        country: unitedStates,
        addressLine1: '720 E Wisconsin Ave',
        addressLine2: '',
        city: 'Milwaukee',
        state: wisconsin,
        zip: '53202',
      },
      name: 'Travel + Leisure',
      status: getCompanyStatusById(CompanyStatusIds.PROPECT),
      industry: _getIDataById('XXI0007', iList),
      subindustry: _getSIDataById('XXSI0011', siList),
      annualRevenue: 37000000000,
      numEmployees: 7500,
      employeeCount: getEmployeeCountById(
        EmployeeCountIds.FIVE_THOUSAND_ONE_TO_TEN_THOUSAND
      ),
      hasLoaOnFile: false,
      loaExpirationDate: '',
      account: _getADataById('XXA0010', aList),
      createdAt: '2024-01-25T21:59:00.000Z',
      modifiedAt: '2024-01-25T21:59:00.000Z',
      version: '1',
      canBeDeleted: false,
      primaryContact: _getCompanyPrimaryCCTDataByCompanyId('XXC0010', cctList),
    },
  ]
}
